/**
 * YogasWidget
 * Displays yogas present and absent, including Mangal Dosha and Kaal Sarp Yoga
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress, Button } from '@mui/joy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getUserPermanentYogas, getUserActiveYogas, storeYogasFromFeeds, type StoredYoga } from '../../utils/yogaStorage';
import { supabase } from '../../utils/supabaseClient';
import { getCachedUser } from '../../utils/cachedUser';
import { generateFeeds, type FunctionalClassification } from '../../astro/feeds/generateFeedsV2';
import type { DashaContext, TransitContext, HouseNum } from '../../astro/feeds/types';

type PlanetName = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

// Type for the dasha context passed from App (before transformation)
interface DashaLevelInfo {
  level: 'MD' | 'AD' | 'PD' | 'SD';
  planet: PlanetName;
  from: string;
  to: string;
  rules: number[];
  house: number;
  aspects: number[];
}

interface InputDashaContext {
  MD: DashaLevelInfo;
  AD: DashaLevelInfo;
  PD: DashaLevelInfo;
  SD: DashaLevelInfo;
  window: { from: string; to: string };
}

interface YogaStatus {
  name: string;
  present: boolean;
  description?: string;
  strength?: 'weak' | 'moderate' | 'strong' | 'very_strong'; // Added very_strong to match StoredYoga
  type?: 'benefic' | 'malefic' | 'neutral' | 'mixed'; // Added mixed to match StoredYoga
  score?: number; // Score for display
  activeFrom?: string; // Active period start
  activeTo?: string; // Active period end
}

interface YogasWidgetProps {
  // Planets in houses
  placements?: Partial<Record<PlanetName, { house: number; offsetDeg: number }>>;
  
  // Ascendant sign (1-12)
  ascendantSign?: number;
  
  // Optional: Pre-computed yoga list
  yogas?: YogaStatus[];
  
  // Optional: Feed items from permanent yogas tab (generated by generateFeeds)
  natalYogaFeeds?: Array<{
    ruleKey?: string;
    title: string;
    description: string;
    type?: 'benefic' | 'malefic' | 'neutral';
    strength?: 'weak' | 'moderate' | 'strong';
  }>;
  
  // Optional: Trigger to refresh yogas from database
  refreshTrigger?: number;
  
  // Data needed for generating yogas
  dashaContext?: InputDashaContext | null;
  functionalClassification?: FunctionalClassification;
  combustionMap?: Partial<Record<PlanetName, boolean>>;
  chartSnapshot?: any;
  houseScores?: Record<number, number> | null;
  
  // Special yogas
  mangalDosha?: {
    present: boolean;
    severity?: 'low' | 'medium' | 'high' | 'cancelled';
    houses?: number[];
  };
  
  kaalSarpYoga?: {
    isPresent?: boolean;
    present?: boolean;
    type?: string;
    severity?: 'partial' | 'full';
    details?: string;
    specificType?: string;
    specificEffects?: string;
    hasRajYogaCombination?: boolean;
    rahuHouse?: number;
    ketuHouse?: number;
    planetsBetween?: string[];
  } | null;
  
  sadeSati?: {
    isActive: boolean;
    currentPhase?: string;
    from?: string;
    to?: string;
  } | null;
  
  // Active yogas from FeedsList (includes doshas and time-activated yogas)
  // This ensures widget displays exactly what appears in Active Yogas tab
  activeYogasFromFeeds?: any[];
}

export default function YogasWidget(props: YogasWidgetProps) {
  const { placements, ascendantSign, yogas, natalYogaFeeds, mangalDosha, kaalSarpYoga, sadeSati, refreshTrigger, dashaContext, functionalClassification, combustionMap, chartSnapshot, houseScores, activeYogasFromFeeds } = props;
  
  // State for stored yogas from database
  const [storedYogas, setStoredYogas] = useState<StoredYoga[]>([]); // Permanent yogas
  const [activeYogas, setActiveYogas] = useState<StoredYoga[]>([]); // Active/time-sensitive yogas
  const [loadingYogas, setLoadingYogas] = useState(true);
  const [generatingYogas, setGeneratingYogas] = useState(false);
  
  // Fetch permanent AND active yogas from database
  useEffect(() => {
    const fetchYogas = async () => {
      setLoadingYogas(true);
      try {
        const user = await getCachedUser(); // Use cached user instead of getUser()
        if (!user) {
          setLoadingYogas(false);
          return;
        }
        
        // Fetch permanent yogas
        const permanentYogas = await getUserPermanentYogas(user.id);
        console.log('[YogasWidget] Fetched permanent yogas:', permanentYogas.length);
        setStoredYogas(permanentYogas);
        
        // Fetch active/time-sensitive yogas based on current date
        // getUserActiveYogas with 'time_sensitive' category filters by active_from/active_to automatically
        const activeYogasData = await getUserActiveYogas(user.id, 'time_sensitive');
        console.log('[YogasWidget] Fetched active time-sensitive yogas:', activeYogasData.length);
        setActiveYogas(activeYogasData);
      } catch (err) {
        console.error('[YogasWidget] Error fetching yogas:', err);
      } finally {
        setLoadingYogas(false);
      }
    };
    
    fetchYogas();
  }, [refreshTrigger]); // Re-fetch when refreshTrigger changes
  
  // Also set loading false when activeYogasFromFeeds is available
  useEffect(() => {
    if (activeYogasFromFeeds && activeYogasFromFeeds.length > 0) {
      setLoadingYogas(false);
    }
  }, [activeYogasFromFeeds]);
  
  // Generate yogas from current chart data
  const handleGenerateYogas = async () => {
    if (!dashaContext || !functionalClassification) {
      alert('Please ensure birth details are calculated first (visit Calculator tab and click "Save & Calculate")');
      return;
    }
    
    setGeneratingYogas(true);
    try {
      const user = await getCachedUser(); // Use cached user
      if (!user) {
        alert('Please sign in to generate yogas');
        return;
      }
      
      // Transform dashaContext to DashaContext structure expected by generateFeeds
      const transformedDasha: DashaContext = {
        levels: [
          { 
            level: 'MD', 
            planet: dashaContext.MD.planet, 
            occupies: dashaContext.MD.house as HouseNum, 
            rules: dashaContext.MD.rules as HouseNum[], 
            aspects: dashaContext.MD.aspects as HouseNum[],
            from: dashaContext.MD.from,
            to: dashaContext.MD.to
          },
          { 
            level: 'AD', 
            planet: dashaContext.AD.planet, 
            occupies: dashaContext.AD.house as HouseNum, 
            rules: dashaContext.AD.rules as HouseNum[], 
            aspects: dashaContext.AD.aspects as HouseNum[],
            from: dashaContext.AD.from,
            to: dashaContext.AD.to
          },
          { 
            level: 'PD', 
            planet: dashaContext.PD.planet, 
            occupies: dashaContext.PD.house as HouseNum, 
            rules: dashaContext.PD.rules as HouseNum[], 
            aspects: dashaContext.PD.aspects as HouseNum[],
            from: dashaContext.PD.from,
            to: dashaContext.PD.to
          },
          { 
            level: 'SD', 
            planet: dashaContext.SD.planet, 
            occupies: dashaContext.SD.house as HouseNum, 
            rules: dashaContext.SD.rules as HouseNum[], 
            aspects: dashaContext.SD.aspects as HouseNum[],
            from: dashaContext.SD.from,
            to: dashaContext.SD.to
          }
        ],
        window: dashaContext.window,
        houseScores: (houseScores || {}) as Record<HouseNum, number>
      };
      
      // Generate feeds
      const transitCtx: TransitContext = {
        positions: [],
        planetsTransitingDashaOccupied: {} as any
      };
      
      const feeds = generateFeeds({
        dasha: transformedDasha,
        transits: transitCtx,
        functional: functionalClassification,
        combustionMap: combustionMap || {}
      });
      
      // Filter natal yogas only
      const natalYogaKeys = new Set([
        'dharma_karma_raj_yoga_natal',
        'lakshmi_yoga_natal',
        'gaja_kesari_yoga_natal',
        'adhi_yoga_natal',
        'budhaditya_yoga_natal',
        'hamsa_yoga_natal',
        'shasha_yoga_natal',
        'ruchaka_yoga_natal',
        'malavya_yoga_natal',
        'bhadra_yoga_natal',
        'neecha_bhanga_raj_yoga_natal',
        'viparita_raja_yoga_natal'
      ]);
      
      const natalYogaFeeds = feeds.filter(f => 
        f.ruleKey?.endsWith('_natal') || 
        natalYogaKeys.has(f.ruleKey || '') ||
        (f.ruleKey?.includes('yoga') && !f.ruleKey?.startsWith('sade_sati'))
      );
      
      console.log('[YogasWidget] Generated', natalYogaFeeds.length, 'natal yogas');
      
      // Store in database
      const result = await storeYogasFromFeeds(natalYogaFeeds, user.id, null, chartSnapshot);
      
      if (result.success) {
        console.log('[YogasWidget] Stored', result.stored, 'yogas');
        // Refresh yogas from database
        const updatedYogas = await getUserPermanentYogas(user.id);
        setStoredYogas(updatedYogas);
      } else {
        console.error('[YogasWidget] Error storing yogas:', result.errors);
        alert('Failed to store yogas: ' + result.errors.join(', '));
      }
    } catch (err) {
      console.error('[YogasWidget] Error generating yogas:', err);
      alert('Error generating yogas. Check console for details.');
    } finally {
      setGeneratingYogas(false);
    }
  };

  // Compute Mangal Dosha if not provided
  const computeMangalDosha = (): YogaStatus => {
    if (mangalDosha !== undefined) {
      if (mangalDosha.present) {
        const houses = mangalDosha.houses?.join(', ') || 'unknown';
        const severity = mangalDosha.severity || 'unknown';
        const isCancelled = severity === 'cancelled';
        
        // Build multi-line description for expandable view
        let desc = `Mars positioned in house ${houses} from Lagna/Moon/Venus\n`;
        desc += `\n🔴 SEVERITY: ${severity.toUpperCase()}\n`;
        
        if (!isCancelled) {
          desc += `\n📝 EFFECTS:\n`;
          if (severity === 'high') {
            desc += `• Significant challenges in marital harmony\n`;
            desc += `• Potential for conflicts and misunderstandings with spouse\n`;
            desc += `• Delays or obstacles in marriage\n`;
          } else if (severity === 'medium') {
            desc += `• Moderate marital tensions\n`;
            desc += `• Some conflicts requiring patience and understanding\n`;
            desc += `• Generally manageable with mutual effort\n`;
          } else {
            desc += `• Mild marital challenges\n`;
            desc += `• Minor conflicts that can be easily resolved\n`;
          }
        } else {
          desc += `\n✅ CANCELLATION CONDITIONS MET\n`;
          desc += `The intensity of Mangal Dosha is significantly reduced due to planetary configurations.\n`;
        }
        
        desc += `\n🙏 REMEDIES:\n`;
        desc += `• Perform Hanuman Chalisa recitation on Tuesdays\n`;
        desc += `• Wear red coral gemstone after consultation\n`;
        desc += `• Observe fast on Tuesdays\n`;
        desc += `• Donate red lentils and red cloth on Tuesdays\n`;
        desc += `• Chant Mars mantras (Om Angarakaya Namaha)\n`;
        desc += `• Match horoscopes carefully before marriage`;
        
        return {
          name: 'Mangal Dosha (Kuja Dosha)',
          present: true,
          description: desc,
          type: isCancelled ? 'neutral' : 'malefic',
          strength: severity === 'high' ? 'strong' : severity === 'medium' ? 'moderate' : 'weak',
        };
      } else {
        return {
          name: 'Mangal Dosha (Kuja Dosha)',
          present: false,
          description: 'Mars not placed in 1st, 2nd, 4th, 7th, 8th, or 12th from Lagna, Moon, or Venus',
          type: 'benefic',
        };
      }
    }

    // Simple check: Mars in 1, 2, 4, 7, 8, 12 from Lagna
    const marsHouse = placements?.Mars?.house;
    if (marsHouse && [1, 2, 4, 7, 8, 12].includes(marsHouse)) {
      let desc = `Mars positioned in ${marsHouse}th house from Lagna\n`;
      desc += `\n🔴 SEVERITY: MODERATE\n`;
      desc += `\n📝 EFFECTS:\n`;
      desc += `• Moderate marital tensions\n`;
      desc += `• Some conflicts requiring patience and understanding\n`;
      desc += `• Generally manageable with mutual effort\n`;
      desc += `\n🙏 REMEDIES:\n`;
      desc += `• Perform Hanuman Chalisa recitation on Tuesdays\n`;
      desc += `• Wear red coral gemstone after consultation\n`;
      desc += `• Observe fast on Tuesdays\n`;
      desc += `• Donate red lentils and red cloth on Tuesdays\n`;
      desc += `• Chant Mars mantras (Om Angarakaya Namaha)`;
      
      return {
        name: 'Mangal Dosha (Kuja Dosha)',
        present: true,
        description: desc,
        type: 'malefic',
        strength: 'moderate',
      };
    }

    return {
      name: 'Mangal Dosha (Kuja Dosha)',
      present: false,
      description: 'Mars not in Dosha-producing houses',
      type: 'benefic',
    };
  };

  // Compute Kaal Sarp Yoga if not provided
  const computeKaalSarpYoga = (): YogaStatus => {
    if (kaalSarpYoga !== undefined && kaalSarpYoga !== null) {
      const isPresent = kaalSarpYoga.isPresent ?? kaalSarpYoga.present ?? false;
      
      let description = '';
      if (isPresent) {
        // Build detailed description with specific type and effects
        if (kaalSarpYoga.specificType) {
          description = `${kaalSarpYoga.specificType} - ${kaalSarpYoga.type || 'Unknown'} type`;
          
          // Use specificEffects if available, otherwise fall back to details
          const effectsText = kaalSarpYoga.specificEffects || kaalSarpYoga.details;
          if (effectsText) {
            description += `\n\n${effectsText}`;
          }
          
          if (kaalSarpYoga.hasRajYogaCombination) {
            description += '\n\n✨ RAJ YOGA: Rahu-Venus combination present - Creates material success and wealth but may challenge love life.';
          }
        } else {
          description = kaalSarpYoga.details || `${kaalSarpYoga.type || 'Unknown type'} (${kaalSarpYoga.severity || 'unknown'} severity)`;
        }
      } else {
        description = 'All planets NOT hemmed between Rahu and Ketu';
      }
      
      return {
        name: kaalSarpYoga.specificType ? kaalSarpYoga.specificType : 'Kaal Sarp Yoga',
        present: isPresent,
        description,
        type: isPresent ? 'malefic' : 'neutral',
        strength: kaalSarpYoga.severity === 'full' || kaalSarpYoga.type === 'full' ? 'strong' : 'weak',
      };
    }

    // Simple check: All 7 planets between Rahu and Ketu
    const rahuHouse = placements?.Rahu?.house;
    const ketuHouse = placements?.Ketu?.house;
    
    if (!rahuHouse || !ketuHouse) {
      return {
        name: 'Kaal Sarp Yoga',
        present: false,
        description: 'Cannot determine (Rahu/Ketu position unavailable)',
        type: 'neutral',
      };
    }

    const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'] as PlanetName[];
    const allBetween = planets.every((p) => {
      const house = placements?.[p]?.house;
      if (!house) return false;
      
      // Check if planet is between Rahu and Ketu (clockwise)
      if (rahuHouse < ketuHouse) {
        return house > rahuHouse && house < ketuHouse;
      } else {
        return house > rahuHouse || house < ketuHouse;
      }
    });

    return {
      name: 'Kaal Sarp Yoga',
      present: allBetween,
      description: allBetween
        ? 'All planets hemmed between Rahu and Ketu'
        : 'Not all planets between Rahu and Ketu',
      type: allBetween ? 'malefic' : 'neutral',
      strength: allBetween ? 'strong' : undefined,
    };
  };

  // Compute Sade Sati if provided
  const computeSadeSati = (): YogaStatus | null => {
    if (!sadeSati) return null;
    
    return {
      name: 'Sade Sati (Saturn Transit)',
      present: sadeSati.isActive,
      description: sadeSati.isActive
        ? `Active ${sadeSati.currentPhase ? `(${sadeSati.currentPhase} phase)` : ''} from ${sadeSati.from || 'unknown'} to ${sadeSati.to || 'unknown'}`
        : 'Not currently active',
      type: sadeSati.isActive ? 'malefic' : 'neutral',
      strength: sadeSati.isActive ? 'moderate' : undefined,
    };
  };

  // Convert natal yoga feeds into YogaStatus format
  const convertNatalYogasToStatus = (): YogaStatus[] => {
    if (!natalYogaFeeds || natalYogaFeeds.length === 0) return [];
    
    return natalYogaFeeds.map(feed => ({
      name: feed.title,
      present: true, // All natal yogas are present (they wouldn't be in the feed otherwise)
      description: feed.description, // No truncation - show full description
      type: feed.type || 'benefic', // Most yogas are benefic
      strength: feed.strength || 'moderate',
    }));
  };
  
  // Convert active yogas from FeedsList into YogaStatus format
  // This is the primary data source - shows exactly what's in Active Yogas tab
  const convertActiveYogasFromFeedsToStatus = (): YogaStatus[] => {
    if (!activeYogasFromFeeds || activeYogasFromFeeds.length === 0) return [];
    
    return activeYogasFromFeeds.map(feed => {
      // Extract title from message
      const titleMatch = feed.message?.match(/^([^:]+):/);
      let title = titleMatch ? titleMatch[1].trim() : '';
      const originalTitle = title; // Keep original for description context
      
      // Post-process extracted title: clean up "between" dates and extra info
      if (title) {
        console.log('[YogasWidget] Original title:', title);
        
        // If title contains "YOGA", extract just up to and including "YOGA"
        const yogaMatch = title.match(/^(.+?YOGA)(?:\s|$)/i);
        if (yogaMatch) {
          title = yogaMatch[1].trim();
          console.log('[YogasWidget] Cleaned yoga title:', title);
        } else {
          // Otherwise, remove everything after "between" keyword
          const betweenMatch = title.match(/^(.+?)\s+between\s+/i);
          if (betweenMatch) {
            title = betweenMatch[1].trim();
            console.log('[YogasWidget] Removed "between" clause:', title);
          }
        }
      }
      
      // If no title found with colon, extract intelligently
      if (!title && feed.message) {
        console.log('[YogasWidget] Extracting title from message:', feed.message.substring(0, 100));
        
        // Strategy 1: Look for text ending with "YOGA" followed by space or end
        const yogaMatch = feed.message.match(/^(.+?YOGA)(?:\s|$)/i);
        if (yogaMatch) {
          title = yogaMatch[1].trim();
          console.log('[YogasWidget] Yoga pattern matched:', title);
        } else {
          // Strategy 2: Look for text before "between" keyword
          const betweenMatch = feed.message.match(/^(.+?)\s+between\s+/i);
          if (betweenMatch) {
            title = betweenMatch[1].trim();
            console.log('[YogasWidget] Between pattern matched:', title);
          } else {
            // Strategy 3: Fall back to first sentence, limited to 60 chars
            const firstSentence = feed.message.split(/[.!?\n]/)[0]?.trim() || '';
            title = firstSentence.length > 60 ? firstSentence.substring(0, 60) + '...' : firstSentence;
            console.log('[YogasWidget] Fallback title:', title);
          }
        }
      }
      
      // Format title properly (handle doshas and yogas)
      if (title.toUpperCase().includes('MANGAL DOSHA')) {
        title = 'Mangal Dosha (Kuja Dosha)';
      } else if (title.toUpperCase().includes('KAAL SARP')) {
        title = 'Kaal Sarp Yoga';
      }
      
      // Build description: if title was cleaned, prepend original title info, then add message body
      let description = '';
      if (titleMatch) {
        // If we cleaned the title (removed dates), include original title with dates in description
        if (originalTitle && originalTitle !== title && originalTitle.toLowerCase().includes('between')) {
          description = originalTitle + '\n\n' + feed.message.substring(titleMatch[0].length).trim();
        } else {
          description = feed.message.substring(titleMatch[0].length).trim();
        }
      } else {
        description = feed.message || feed.description || '';
      }
      
      // Determine type and strength from feed data
      const type = feed.severity === 'warning' || feed.severity === 'critical' 
        ? 'malefic' 
        : feed.severity === 'info' || feed.severity === 'benefit'
        ? 'benefic'
        : 'neutral';
      
      const strength = feed.score && Math.abs(feed.score) > 50 
        ? 'strong' 
        : feed.score && Math.abs(feed.score) > 30
        ? 'moderate'
        : 'weak';
      
      return {
        name: title,
        present: true, // All items in activeYogasFromFeeds are present
        description,
        type,
        strength,
        score: feed.score,
        activeFrom: feed.window?.from,
        activeTo: feed.window?.to,
      };
    });
  };
  
  // Convert stored yogas from database into YogaStatus format
  const convertStoredYogasToStatus = (): YogaStatus[] => {
    if (!storedYogas || storedYogas.length === 0) return [];
    
    return storedYogas.map(yoga => ({
      name: yoga.yoga_name,
      present: yoga.is_present,
      score: yoga.score, // Add score for display
      description: yoga.description || '',
      type: yoga.type || 'neutral',
      strength: yoga.strength || 'moderate',
      activeFrom: yoga.active_from || undefined,
      activeTo: yoga.active_to || undefined,
    }));
  };
  
  // Convert active yogas from database into YogaStatus format
  const convertActiveYogasToStatus = (): YogaStatus[] => {
    if (!activeYogas || activeYogas.length === 0) return [];
    
    return activeYogas.map(yoga => ({
      name: `${yoga.yoga_name} (Active Now)`,
      present: yoga.is_present,
      description: yoga.description || '',
      type: yoga.type || 'neutral',
      strength: 'strong', // Active yogas are always strong since they're manifesting now
      score: yoga.score,
      activeFrom: yoga.active_from || undefined,
      activeTo: yoga.active_to || undefined,
    }));
  };

  // Combine all yogas and remove duplicates by name
  // Widget shows active yogas from Active Yogas tab + computed absent doshas
  const allYogasWithDuplicates: YogaStatus[] = [
    ...convertActiveYogasFromFeedsToStatus(), // From Active Yogas tab (present items only)
    computeMangalDosha(), // Check if absent
    computeKaalSarpYoga(), // Check if absent
  ].filter((y): y is YogaStatus => y !== null);
  
  // Remove duplicates by normalized name only (not description)
  // This catches cases like "Mangal Dosha (Kuja Dosha)" appearing multiple times
  const seenNames = new Set<string>();
  const allYogas = allYogasWithDuplicates.filter(yoga => {
    // Create unique key from normalized name only
    const normalizedName = yoga.name.trim().toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/\(.*?\)/g, '') // Remove parentheses content
      .trim();
    
    if (seenNames.has(normalizedName)) {
      return false; // Skip duplicate
    }
    seenNames.add(normalizedName);
    return true;
  });

  // Sort to prioritize doshas at the top
  const presentYogas = allYogas
    .filter((y) => y.present)
    .sort((a, b) => {
      const aIsDosha = a.name.toLowerCase().includes('dosha');
      const bIsDosha = b.name.toLowerCase().includes('dosha');
      
      if (aIsDosha && !bIsDosha) return -1; // a comes first
      if (!aIsDosha && bIsDosha) return 1;  // b comes first
      return 0; // maintain original order for non-doshas
    });
  
  const absentYogas = allYogas.filter((y) => !y.present);

  const getYogaColor = (yoga: YogaStatus) => {
    if (!yoga.present) return 'neutral';
    
    // Check if it's a dosha by name
    const isDosha = yoga.name.toLowerCase().includes('dosha');
    if (isDosha) return 'danger'; // Doshas are red
    
    // Otherwise use type-based coloring
    if (yoga.type === 'benefic') return 'success'; // Yogas are green
    if (yoga.type === 'malefic') return 'danger';
    return 'success'; // Default to green for positive yogas (changed from primary/blue)
  };

  const getStrengthBadge = (strength?: string) => {
    if (!strength) return null;
    const color = strength === 'strong' ? 'danger' : strength === 'moderate' ? 'warning' : 'neutral';
    return (
      <Chip size="sm" color={color} variant="soft" sx={{ ml: 1 }}>
        {strength}
      </Chip>
    );
  };

  return (
    <Box sx={{ 
      fontSize: '0.875rem',
      maxHeight: '600px', // Max height for scrolling
      overflowY: 'auto', // Vertical scroll
      pr: 0.5, // Padding for scrollbar
      width: '100%',
      boxSizing: 'border-box',
    }}>
      {/* Generate Yogas Button */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          size="sm"
          variant="soft"
          color="primary"
          startDecorator={<RefreshIcon />}
          onClick={handleGenerateYogas}
          loading={generatingYogas}
          disabled={generatingYogas || !dashaContext}
        >
          {generatingYogas ? 'Generating...' : 'Generate Yogas from Chart'}
        </Button>
        {!dashaContext && (
          <Typography level="body-xs" sx={{ color: 'warning.500' }}>
            Calculate birth chart first
          </Typography>
        )}
      </Box>
      
      {/* Loading state */}
      {loadingYogas && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm">Loading yogas...</Typography>
        </Box>
      )}
      
      {/* Present Yogas */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <CheckCircleIcon sx={{ mr: 0.5, color: 'success.500', fontSize: '1.25rem' }} />
          <Typography level="title-sm" sx={{ fontWeight: 700 }}>
            Active Yogas & Doshas ({presentYogas.length})
          </Typography>
          <Typography 
            level="body-sm" 
            component="a"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              // Dispatch event to navigate to Current Period & Beyond → Active Yogas section
              window.dispatchEvent(new CustomEvent('navigate:activeYogas'));
            }}
            sx={{ 
              color: 'primary.500', 
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '0.75rem',
              '&:hover': { color: 'primary.700' }
            }}
          >
            (View Details)
          </Typography>
        </Box>

        {presentYogas.length === 0 ? (
          <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'neutral.500' }}>
            {loadingYogas ? 'Loading...' : 'No significant yogas detected. Click "Generate Yogas" to analyze your chart.'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {presentYogas.map((yoga, idx) => {
              // Extract short description (first sentence or first 250 chars)
              let shortDesc = '';
              if (yoga.description) {
                const lines = yoga.description.split('\n').map(l => l.trim()).filter(l => l);
                // Skip lines that look like the original title with dates (duplicates)
                const contentLines = lines.filter(l => 
                  !l.toLowerCase().includes('between') || 
                  !l.match(/\d{1,2}(?:st|nd|rd|th)?\s+\w{3}\s+\d{4}/)
                );
                const firstLine = contentLines[0] || lines[0] || '';
                // Show full first sentence or up to 250 chars
                const sentenceEnd = firstLine.indexOf('. ');
                if (sentenceEnd > 0 && sentenceEnd < 250) {
                  shortDesc = firstLine.substring(0, sentenceEnd + 1);
                } else {
                  shortDesc = firstLine.length > 250 ? firstLine.substring(0, 250) + '...' : firstLine;
                }
              }
              
              return (
                <Box key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                      size="sm"
                      color={getYogaColor(yoga)}
                      variant="soft"
                      sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                    >
                      {/* Add emoji icon based on yoga type */}
                      {yoga.name.toLowerCase().includes('vasumati') && '💰 '}
                      {yoga.name.toLowerCase().includes('raj yoga') && '👑 '}
                      {yoga.name.toLowerCase().includes('gaja kesari') && '🐘 '}
                      {yoga.name.toLowerCase().includes('hamsa') && '🦢 '}
                      {yoga.name.toLowerCase().includes('budhaditya') && '☿️ '}
                      {yoga.name.toLowerCase().includes('lakshmi') && '💎 '}
                      {yoga.name.toLowerCase().includes('dosha') && '🔴 '}
                      {yoga.name}
                    </Chip>
                    {getStrengthBadge(yoga.strength)}
                    {yoga.score !== undefined && (
                      <Chip size="sm" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                        {yoga.score > 0 ? '+' : ''}{Math.round(yoga.score)} pts
                      </Chip>
                    )}
                  </Box>
                  {/* Short description */}
                  {shortDesc && (
                    <Typography level="body-sm" sx={{ color: 'neutral.600', lineHeight: 1.6 }}>
                      {shortDesc}
                    </Typography>
                  )}
                  {/* Active period */}
                  {(yoga.activeFrom || yoga.activeTo) && (
                    <Typography level="body-xs" sx={{ color: 'primary.500', fontWeight: 500, mt: 0.5 }}>
                      ⏰ Active: {yoga.activeFrom ? new Date(yoga.activeFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '?'} → {yoga.activeTo ? new Date(yoga.activeTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '?'}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Absent Yogas */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CancelIcon sx={{ mr: 1, color: 'neutral.400', fontSize: '1.25rem' }} />
          <Typography level="title-sm" sx={{ fontWeight: 700, color: 'neutral.700' }}>
            Absent Yogas ({absentYogas.length})
          </Typography>
        </Box>

        {absentYogas.length === 0 ? (
          <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'neutral.500' }}>
            All checked yogas are present
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {absentYogas.map((yoga, idx) => (
              <Chip
                key={idx}
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ opacity: 0.7, fontSize: '0.75rem' }}
              >
                {yoga.name}
              </Chip>
            ))}
          </Box>
        )}
      </Box>

      <Typography level="body-xs" sx={{ color: 'neutral.500', fontStyle: 'italic', mt: 2 }}>
        Note: Yoga analysis is simplified. For comprehensive analysis, consult with an astrologer.
      </Typography>
    </Box>
  );
}
